import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, Trash2, Mail } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  
  // Import Google Font Caveat
  React.useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const sections = [
    {
      icon: <Database className="w-6 h-6 text-teal-500" />,
      title: "Données collectées",
      content: `Nous collectons uniquement les données nécessaires au fonctionnement de l'application :
      
      • **Informations de compte** : email, prénom, nom
      • **Profils enfants** : prénom, âge, genre (pour personnaliser le contenu)
      • **Historique d'utilisation** : conversations et histoires générées
      • **Données de paiement** : traitées par Stripe (nous ne stockons pas vos coordonnées bancaires)`
    },
    {
      icon: <Lock className="w-6 h-6 text-teal-500" />,
      title: "Protection des données",
      content: `La sécurité de vos données est notre priorité :
      
      • Chiffrement SSL/TLS pour toutes les communications
      • Base de données sécurisée avec accès restreint
      • Authentification par token JWT
      • Aucun partage avec des tiers non autorisés
      • Hébergement conforme au RGPD`
    },
    {
      icon: <Eye className="w-6 h-6 text-teal-500" />,
      title: "Utilisation des données",
      content: `Vos données sont utilisées exclusivement pour :
      
      • Personnaliser les histoires et conversations selon l'âge de l'enfant
      • Sauvegarder vos histoires favorites et historique
      • Améliorer la qualité de nos services
      • Vous envoyer des communications importantes (désactivables)
      
      **Nous ne vendons jamais vos données à des tiers.**`
    },
    {
      icon: <Trash2 className="w-6 h-6 text-teal-500" />,
      title: "Vos droits",
      content: `Conformément au RGPD, vous disposez des droits suivants :
      
      • **Droit d'accès** : consultez vos données à tout moment
      • **Droit de rectification** : modifiez vos informations
      • **Droit à l'effacement** : supprimez votre compte et vos données
      • **Droit à la portabilité** : exportez vos données
      • **Droit d'opposition** : refusez certains traitements
      
      Pour exercer ces droits, contactez-nous à contact@dismaman.com`
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors mr-4"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Caveat", cursive' }}>
            Politique de confidentialité
          </h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Introduction */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-lg" data-testid="privacy-intro-section">
          <div className="flex items-center mb-4">
            <Shield className="w-8 h-8 text-teal-500 mr-3" />
            <h2 className="text-xl font-bold text-gray-800">Votre vie privée est importante</h2>
          </div>
          <p className="text-gray-600">
            Chez Dis Maman!, nous prenons la protection de vos données personnelles très au sérieux. 
            Cette politique explique comment nous collectons, utilisons et protégeons vos informations.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Dernière mise à jour : Avril 2026
          </p>
        </div>

        {/* Sections */}
        {sections.map((section, index) => (
          <div 
            key={index} 
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-6 shadow-lg"
            data-testid={`privacy-section-${index}`}
          >
            <div className="flex items-center mb-4">
              {section.icon}
              <h2 className="text-xl font-bold text-gray-800 ml-3">{section.title}</h2>
            </div>
            <div className="text-gray-600 whitespace-pre-line">
              {section.content.split('**').map((part, i) => 
                i % 2 === 0 ? part : <strong key={i}>{part}</strong>
              )}
            </div>
          </div>
        ))}

        {/* Cookies */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-lg" data-testid="privacy-cookies-section">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Cookies et technologies similaires</h2>
          <p className="text-gray-600 mb-4">
            Nous utilisons des cookies essentiels pour le fonctionnement de l'application :
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2">
            <li><strong>Cookies d'authentification</strong> : pour maintenir votre session</li>
            <li><strong>Cookies de préférences</strong> : pour mémoriser vos choix</li>
          </ul>
          <p className="text-gray-600 mt-4">
            Nous n'utilisons pas de cookies publicitaires ou de tracking tiers.
          </p>
        </div>

        {/* Contact */}
        <div className="bg-gradient-to-r from-blue-500 to-teal-500 rounded-2xl p-6 text-white shadow-lg" data-testid="privacy-contact-section">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Mail className="w-6 h-6 mr-2" />
            Des questions ?
          </h2>
          <p className="mb-4">
            Si vous avez des questions concernant cette politique de confidentialité ou vos données, 
            n'hésitez pas à nous contacter.
          </p>
          <a 
            href="mailto:contact@dismaman.com"
            className="inline-flex items-center px-6 py-3 bg-white text-teal-600 rounded-xl font-medium hover:bg-gray-100 transition-colors"
          >
            <Mail className="w-5 h-5 mr-2" />
            contact@dismaman.com
          </a>
        </div>

        {/* Footer info */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>Dis Maman! - Application conforme au RGPD</p>
          <p>© 2024 Dis Maman! - Tous droits réservés</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
